import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {projectGetAll, projectExcel} from '../../../redux/slices/project.slice'
import {basicLookup} from '../../../redux/slices/lookup.slice'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectProjectData = (state) => ({
  project: state.project.project,
  loading: state.project.loading
})

const selectLookupData = (state) => ({
  ProjectState: state.lookup.ProjectState
})

function List() {
  const intl = useIntl()
  const {project, loading} = useSelector(selectProjectData)
  const {ProjectState} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(projectGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(basicLookup('ProjectState'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      projectExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.PROJECTS'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROJECT.SEARCH.PROJECT_NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROJECT.SEARCH.STARTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'startDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROJECT.SEARCH.REMAININGDAY'})}
          className='min-w-125px'
        />
      ),
      accessor: 'remainingDay',
      Cell: ({...props}) => <CustomCell item={props} computation='remainingDay' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PROJECT.SEARCH.PROJECTSTATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectStateName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'PROJECT.SEARCH.PROJECTSTATE',
          data: ProjectState,
          name: 'projectStateId',
        },
      ],
    },
  ], [handleExcelExport, ProjectState])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.PROJECTS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'PROJECTS'}
        />
        <KTCardBody>
          {!project && <ListLoading />}
          {project && (
            <Table
              items={project}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
